import React, { useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import { Fattura, Clienti, Profile } from '../types';
import apiService from '../services/api';

interface FatturaPDFProps {
  fattura: Fattura;
  cliente: Clienti;
  profile: Profile;
  storicoId: number; // il PDF generato viene salvato su questa fattura
  autoSalva?: boolean; // genera e salva il PDF appena montato, senza scaricarlo
  onSalvato?: (ok: boolean) => void;
}

const FatturaPDF: React.FC<FatturaPDFProps> = ({ fattura, cliente, profile, storicoId, autoSalva, onSalvato }) => {
  const pdfRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Genera il PDF usando jsPDF e html2canvas
  const creaPdf = async (): Promise<jsPDF> => {
    if (!pdfRef.current) {
      throw new Error('Elemento referenza non trovato');
    }

    // Converti l'HTML in canvas
    // Ridotto scale a 1.5 per bilanciare qualità e dimensione file
    const canvas = await html2canvas(pdfRef.current, {
      scale: 1.5, // Compromesso ottimale tra qualità e dimensione
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      logging: false, // Disabilita logging per migliori performance
      imageTimeout: 0
    });

    // Crea il PDF
    // Usa JPEG invece di PNG per ridurre drasticamente la dimensione
    // Qualità 0.92 offre un ottimo compromesso qualità/dimensione
    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    const pdf = new jsPDF('p', 'mm', 'a4');

    const imgWidth = 210; // A4 width in mm
    const pageHeight = 295; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    let position = 0;

    // Prima pagina
    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Pagine aggiuntive se necessario
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    return pdf;
  };

  const nomeFile = `fattura-${fattura.numero}.pdf`;

  const generatePDF = async () => {
    const button = buttonRef.current;
    try {
      if (button) {
        button.disabled = true;
        button.textContent = '⏳ Generazione PDF...';
      }

      const pdf = await creaPdf();
      pdf.save(nomeFile);

      try {
        await apiService.salvaPdfDocumento('fattura', storicoId, pdf.output('blob'), nomeFile);
      } catch (uploadError) {
        console.error('Errore durante l\'upload del PDF al server:', uploadError);
      }
    } catch (error) {
      console.error('Errore nella generazione del PDF:', error);
      alert('Si è verificato un errore nella generazione del PDF. Riprova.');
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = '📥 Download PDF';
      }
    }
  };

  // Generazione automatica (usata dalla finestra email): crea e salva il PDF senza scaricarlo
  const autoAvviato = useRef(false);
  useEffect(() => {
    if (!autoSalva || autoAvviato.current) return;
    autoAvviato.current = true;
    creaPdf()
      .then((pdf) => apiService.salvaPdfDocumento('fattura', storicoId, pdf.output('blob'), nomeFile))
      .then(() => onSalvato?.(true))
      .catch((error) => {
        console.error('Errore nella generazione automatica del PDF:', error);
        onSalvato?.(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      <div className="mb-3">
        <Button 
          variant="primary" 
          className="me-2"
          onClick={generatePDF}
          ref={buttonRef}
        >
          📥 Download PDF
        </Button>
        <Button variant="outline-primary" onClick={handlePrint}>
          🖨️ Stampa
        </Button>
      </div>

      {/* Contenuto per la generazione PDF - nascosto per la stampa */}
      <div ref={pdfRef} style={{ backgroundColor: 'white', padding: '40px', fontFamily: 'Arial, sans-serif', minHeight: '100vh' }}>
        <div style={{ backgroundColor: 'white', maxWidth: '800px', margin: '0 auto' }}>
          {/* Header Fattura */}
          <div style={{ marginBottom: '50px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h1 style={{ fontSize: '36px', fontWeight: 'bold', margin: '0', color: '#1e40af' }}>Fattura</h1>
                <p style={{ fontSize: '18px', margin: '10px 0 0 0', color: '#374151' }}>N: {fattura.numero}/{fattura.anno}</p>
              </div>
            </div>
          </div>

          {/* Sezioni Principali */}
          <div style={{ display: 'flex', gap: '60px', marginBottom: '50px' }}>
            {/* Colonna Sinistra - Cliente */}
            <div style={{ flex: '1' }}>
              {/* Cliente */}
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 15px 0', color: '#1e40af' }}>Cliente</h3>
                <div style={{ lineHeight: '1.6' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' }}>{cliente.nome} {cliente.cognome}</div>
                  <div style={{ fontSize: '14px', marginBottom: '5px' }}>{cliente.indirizzo} - {cliente.citta}</div>
                  <div style={{ fontSize: '14px' }}><strong>Codice Fiscale:</strong> {cliente.codice_fiscale}</div>
                </div>
              </div>
            </div>

            {/* Colonna Destra - Fornitore */}
            <div style={{ flex: '1' }}>
              {/* Fornitore */}
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 15px 0', color: '#1e40af' }}>Da</h3>
                <div style={{ lineHeight: '1.6' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' }}>{profile.first_name} {profile.last_name}</div>
                  <div style={{ fontSize: '14px', marginBottom: '5px' }}>{profile.indirizzo}</div>
                  <div style={{ fontSize: '14px', marginBottom: '5px' }}><strong>Codice Fiscale:</strong> {profile.codice_fiscale || 'None'}</div>
                  <div style={{ fontSize: '14px', marginBottom: '5px' }}><strong>P.Iva:</strong> {profile.piva}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Sezioni Pagamento e Data - Allineate orizzontalmente */}
          <div style={{ display: 'flex', gap: '60px', marginBottom: '50px' }}>
            {/* Pagamento */}
            <div style={{ flex: '1' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 15px 0', color: '#1e40af' }}>Pagamento</h3>
              <div style={{ lineHeight: '1.6' }}>
                <div style={{ fontSize: '14px', marginBottom: '5px' }}><strong>Metodo:</strong> {fattura.tipo_pagamento}</div>
                <div style={{ fontSize: '14px' }}><strong>Effettuato:</strong> Si</div>
              </div>
            </div>

            {/* Data */}
            <div style={{ flex: '1' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 15px 0', color: '#1e40af' }}>Data</h3>
              <div style={{ lineHeight: '1.6' }}>
                <div style={{ fontSize: '14px' }}><strong>Data Fattura:</strong> {fattura.data}</div>
              </div>
            </div>
          </div>

          {/* Dettaglio prestazione */}
          <div style={{ marginBottom: '50px' }}>
            {/* Barra blu con titolo */}
            <div style={{ 
              backgroundColor: '#1e40af', 
              color: 'white', 
              padding: '18px 25px', 
              fontSize: '18px', 
              fontWeight: 'bold',
              marginBottom: '0'
            }}>
              Dettaglio prestazione
            </div>
            
            {/* Tabella */}
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db' }}>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid #d1d5db', padding: '18px 25px', fontSize: '16px', width: '60%' }}>Visita cardiologica</td>
                  <td style={{ border: '1px solid #d1d5db', padding: '18px 25px', fontSize: '16px', textAlign: 'center', width: '20%' }}></td>
                  <td style={{ border: '1px solid #d1d5db', padding: '18px 25px', fontSize: '16px', textAlign: 'right', width: '20%' }}></td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #d1d5db', padding: '18px 25px', fontSize: '16px' }}>Onorario</td>
                  <td style={{ border: '1px solid #d1d5db', padding: '18px 25px', fontSize: '16px', textAlign: 'center' }}></td>
                  <td style={{ border: '1px solid #d1d5db', padding: '18px 25px', fontSize: '16px', textAlign: 'right' }}>€ {fattura.onorario}</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #d1d5db', padding: '18px 25px', fontSize: '16px' }}>Contributo ENPAV</td>
                  <td style={{ border: '1px solid #d1d5db', padding: '18px 25px', fontSize: '16px', textAlign: 'center' }}>2%</td>
                  <td style={{ border: '1px solid #d1d5db', padding: '18px 25px', fontSize: '16px', textAlign: 'right' }}>€ {fattura.contributo_enpav}</td>
                </tr>
                <tr style={{ backgroundColor: '#1e40af', color: 'white' }}>
                  <td style={{ border: '1px solid #1e40af', padding: '18px 25px', fontSize: '18px', fontWeight: 'bold' }}>Totale fattura</td>
                  <td style={{ border: '1px solid #1e40af', padding: '18px 25px', fontSize: '18px', fontWeight: 'bold', textAlign: 'center' }}></td>
                  <td style={{ border: '1px solid #1e40af', padding: '18px 25px', fontSize: '18px', fontWeight: 'bold', textAlign: 'right' }}>€ {fattura.totale}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Termini */}
          <div style={{ marginTop: '60px', marginBottom: '40px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: '0 0 20px 0', color: '#000' }}>Termini</h3>
            <p style={{ fontSize: '15px', lineHeight: '1.8', margin: '0', color: '#374151' }}>
              Prestazione svolta in regime forfettario ai sensi dell'art.1, commi 54-88, legge 190/2014 così come modificata dalle leggi 208/2015 e 145/2018
            </p>
          </div>

          {/* Spazio aggiuntivo in fondo */}
          <div style={{ height: '60px' }}></div>
        </div>
      </div>
    </div>
  );
};

export default FatturaPDF; 